import { useMemo } from 'react';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { nurtureBuckets, type NurtureBucket } from '@/domain/nurture';
import { DEFAULT_NURTURE_TEMPLATES, NURTURE_SEGMENTS } from '@/domain/nurtureFlow';

/**
 * Nurture (Prompt K, K4) - lists only. Every past customer sits in exactly one
 * cadence bucket (monthly / quarterly / yearly / dead pile), derived from the
 * engagement bands the nurture re-engagement lists already use: a count and the
 * customer list per bucket. The editable per-segment SMS copy sits above (the
 * JustCall seam sends it; the reds-recycle feeder drives the sends).
 */

const CAP = 8;

export default function Nurture() {
  const tailRecords = useBrainStore((s) => s.tailRecords);
  const studios = useBrainStore((s) => s.studios);

  const buckets = useMemo(() => nurtureBuckets(tailRecords), [tailRecords]);
  const studioName = (id: string) => studios[id]?.name ?? id.replace('-', ' ');

  return (
    <section className="px-8 py-8" data-testid="nurture">
      <header className="max-w-6xl">
        <p className="label-small-caps text-gold-bright">Growth · Nurture</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Nurture</h1>
        <p className="font-serif text-cream-soft mt-3 max-w-2xl">
          Every past customer, sorted into one contact cadence by how recently they engaged. Lists
          only for now — who sits where, so you know who is due a nudge.
        </p>
      </header>

      <div className="mt-8 max-w-6xl">
        <NurtureTemplatesEditor />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-6xl">
        {buckets.map((bucket) => (
          <BucketPanel key={bucket.cadence} bucket={bucket} studioName={studioName} />
        ))}
      </div>
    </section>
  );
}

/** Editable per-segment nurture SMS copy (historic / light-red / dark-red). */
function NurtureTemplatesEditor() {
  const templates = useBrainStore((s) => s.nurtureTemplates);
  const setTemplate = useBrainStore((s) => s.setNurtureTemplate);
  return (
    <div className="panel p-5 max-w-3xl" data-testid="nurture-templates">
      <h2 className="heading-serif text-lg text-cream">Message copy</h2>
      <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-1">
        Historic and light reds by SMS · dark reds by light-touch email (not switched on yet) ·
        variables {'{name} {artist} {studio}'}
      </p>
      <div className="mt-4 space-y-4">
        {NURTURE_SEGMENTS.map((seg) => (
          <div key={seg}>
            <label
              htmlFor={`nurture-template-${seg}`}
              className="font-inter text-xs text-cream-soft"
            >
              {DEFAULT_NURTURE_TEMPLATES[seg].label}
            </label>
            <textarea
              id={`nurture-template-${seg}`}
              data-testid={`nurture-template-${seg}`}
              value={templates[seg] ?? DEFAULT_NURTURE_TEMPLATES[seg].body}
              onChange={(e) => setTemplate(seg, e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-gold-dim/40 bg-ink-soft/30 p-2 text-sm text-cream font-inter"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function BucketPanel({
  bucket,
  studioName,
}: {
  bucket: NurtureBucket;
  studioName: (id: string) => string;
}) {
  const shown = bucket.records.slice(0, CAP);
  const remaining = bucket.records.length - shown.length;
  return (
    <div className="panel p-5" data-testid={`nurture-bucket-${bucket.cadence}`}>
      <div className="flex items-baseline justify-between">
        <h2 className="heading-serif text-lg text-cream">{bucket.label}</h2>
        <span
          className="font-cinzel text-2xl text-gold-bright tabular-nums"
          data-testid={`nurture-count-${bucket.cadence}`}
        >
          {bucket.records.length.toLocaleString('en-AU')}
        </span>
      </div>
      <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-1">
        {bucket.blurb}
      </p>

      {shown.length === 0 ? (
        <p className="font-serif text-cream-soft text-sm mt-4">No customers in this bucket.</p>
      ) : (
        <ul className="mt-3 divide-y divide-gold-dim/15">
          {shown.map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-3 py-2">
              <div>
                <p className="font-inter text-sm text-cream">
                  {r.firstName} {r.lastName}
                </p>
                <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-0.5">
                  {r.suburb} · {r.designType}
                </p>
              </div>
              <div className="text-right whitespace-nowrap">
                <p className="font-mono text-[11px] text-cream-soft">
                  {format(new Date(r.lastInteractionAt), 'd MMM yyyy')}
                </p>
                <p className="font-inter text-[10px] text-cream-faint">{studioName(r.primaryStudioId)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {remaining > 0 ? (
        <p className="font-inter text-[11px] text-cream-faint mt-3">
          + {remaining.toLocaleString('en-AU')} more
        </p>
      ) : null}
    </div>
  );
}
